// Unica copia de este texto: la sirven j2l-website (pagina publica y drawers de los footers) y j2l-identity
// (drawer del registro), cada una compilandola contra su propio React y antd.
//
// Al aceptarse en el registro, de este texto se guarda la VERSION, no el contenido: cada alta escribe en
// `UsersConsents` la que `LegalDocuments` (j2l-identity, Jobs2Live.Identity.Domain) declara vigente. Si cambias
// algo de aqui, sube alli `UseAgreementVersion`, o lo que quede guardado dejara de describir lo que el usuario tuvo
// delante. Subirla no le vuelve a pedir el consentimiento a nadie.
//
// Este aviso NO es un control:
//
//   - La constante vive en OTRO repositorio, asi que una PR de aqui no la tiene delante.
//   - El texto va envuelto en `t()`, asi que una edicion puede llegar como cambio de `translations.json` de
//     cualquiera de las dos aplicaciones, donde ningun comentario en un .tsx la ve.

import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from 'antd';
import { Paragraph, Title, Text, Link } from '../typography';

import styles from './use-agreement.module.scss';

export interface UseAgreementProps {
  title1FontSize?: number;
  title2FontSize?: number;
  title3FontSize?: number;
  normalTextFontSize?: number;
}

const UseAgreement: FC<UseAgreementProps> = ({ title1FontSize = 30, title2FontSize = 20, title3FontSize = 18, normalTextFontSize = 16 }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.wrapper}>
      <div className={styles.wrapperContainer}>
        <Typography>
          <Title fontSize={title1FontSize}>{t('Aviso legal y condiciones de uso')}</Title>
          <Title level={2} fontSize={title2FontSize}>
            1. {t('Aviso legal y aceptación')}
          </Title>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'Este Aviso Legal regula el acceso y utilización del portal de Internet correspondiente a la dirección: (en adelante, el "Portal") cuyo responsable es:'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              <ul className={styles.indentedUl}>
                <li>{t('Identidad: AWESOME COLLABORATIVE MARKET PLACE, S.L.')}</li>
                <li>{t('N.I.F. B44638666')}</li>
                <li>{t('Información Mercantil: Registro Mercantil de Madrid, Tomo 44759, Folio 136, Inscripción 1ª, Hoja M-788106.')}</li>
                <li>
                  {t('Correo electrónico')}:&nbsp;
                  <Link underline fontSize={normalTextFontSize} color="#1A1C21" href="mailto:privacidad@jobs2live.com">
                    privacidad@jobs2live.com
                  </Link>
                </li>
              </ul>
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'El acceso a la página web es gratuito salvo en lo relativo al coste de la conexión a través de la red de telecomunicaciones suministrada por el proveedor de acceso contratado por los usuarios. La utilización del Portal atribuye la condición de usuario del Portal (en adelante, el "Usuario") e implica la aceptación de todas las condiciones incluidas en este Aviso Legal.'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'La prestación del servicio del Portal tiene una duración limitada al momento en el que el Usuario se encuentre conectado al mismo o a alguno de los servicios que a través del mismo se facilitan. Por tanto, el Usuario debe leer atentamente el presente Aviso Legal en cada una de las ocasiones en que se proponga utilizar el Portal, ya que éste y sus condiciones de uso pueden sufrir modificaciones.'
              )}
            </Text>
          </Paragraph>

          <Title level={2} fontSize={title2FontSize}>
            2. {t('Propiedad intelectual e industrial')}
          </Title>

          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'Todos los contenidos del Portal, entendiendo por estos a título meramente enunciativo los textos, fotografías, gráficos, imágenes, iconos, tecnología, software, links y demás contenidos audiovisuales o sonoros, así como su diseño gráfico y códigos fuente (en adelante, los "Contenidos"), son propiedad intelectual del Prestador de Servicios o de terceros, sin que puedan entenderse cedidos al Usuario ninguno de los derechos de explotación reconocidos por la normativa vigente en materia de propiedad intelectual sobre los mismos, reconociéndose únicamente el mero uso de aquellos que resulten estrictamente necesarios para la utilización del Portal.'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'Las marcas, nombres comerciales o signos distintivos son titularidad del Prestador de Servicios o terceros, sin que pueda entenderse que el acceso al Portal atribuya ningún derecho sobre las citadas marcas, nombres comerciales y/o signos distintivos.'
              )}
            </Text>
          </Paragraph>

          <Title level={2} fontSize={title2FontSize}>
            3. {t('Condiciones de uso del Portal')}
          </Title>
          <Title level={3} fontSize={title3FontSize}>
            3.1 {t('General')}
          </Title>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'El Usuario se obliga a hacer un uso correcto del Portal de conformidad con la Ley y el presente Aviso Legal. El Usuario responderá frente al Prestador de Servicios o frente a terceros, de cualesquiera daños y perjuicios que pudieran causarse como consecuencia del incumplimiento de dicha obligación.'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'Queda expresamente prohibido el uso del Portal con fines lesivos para los bienes o intereses del Prestador de Servicios o de terceros, o que de cualquier otra forma sobrecarguen, dañen o inutilicen las redes, servidores y demás equipos informáticos (hardware) o productos y aplicaciones informáticas (software) del Prestador de Servicios o de terceros.'
              )}
            </Text>
          </Paragraph>

          <Title level={3} fontSize={title3FontSize}>
            3.2 {t('Contenidos')}
          </Title>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'El Usuario se compromete a utilizar los Contenidos de conformidad con la Ley y el presente Aviso Legal, así como con las demás condiciones, reglamentos e instrucciones que en su caso pudieran ser de aplicación.'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t('Con carácter meramente enunciativo, el Usuario de acuerdo con la legislación vigente deberá abstenerse de:')}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              <ul className={styles.indentedUl}>
                <li>
                  {t(
                    'Reproducir, copiar, distribuir, poner a disposición, comunicar públicamente, transformar o modificar los Contenidos salvo en los casos autorizados en la ley o expresamente consentidos por el Prestador de Servicios o por quien ostente la titularidad de los derechos de explotación en su caso.'
                  )}
                </li>
                <li>
                  {t(
                    'Reproducir o copiar para uso privado los Contenidos que puedan ser considerados como Software o Base de Datos de conformidad con la legislación vigente en materia de propiedad intelectual, así como su comunicación pública o puesta a disposición de terceros cuando estos actos impliquen necesariamente la reproducción por parte del Usuario o de un tercero.'
                  )}
                </li>
                <li>
                  {t(
                    'Extraer y/o reutilizar la totalidad o una parte sustancial de los Contenidos integrantes del Portal, así como de las bases de datos que el Prestador de Servicios ponga a disposición de los Usuarios.'
                  )}
                </li>
              </ul>
            </Text>
          </Paragraph>

          <Title level={3} fontSize={title3FontSize}>
            3.3 {t('Introducción de enlaces al Portal')}
          </Title>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'El usuario de Internet que quiera introducir enlaces desde sus propias páginas web al Portal deberá cumplir con las condiciones que se detallan a continuación sin que el desconocimiento de las mismas evite las responsabilidades derivadas de la normativa que resulte aplicable.'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'El enlace únicamente vinculará con la "home page" o página principal del Portal pero no podrá reproducirla de ninguna forma (inline links, copia de los textos, gráficos, etc.)'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'Quedará en todo caso prohibido, de acuerdo con la legislación aplicable y vigente en cada momento, establecer "frames" o marcos de cualquier tipo que envuelvan al Portal o permitan la visualización de los Contenidos a través de direcciones de Internet distintas a las del Portal y, en cualquier caso, cuando se visualicen conjuntamente con contenidos ajenos al Portal de forma que: (I) produzca, o pueda producir, error, confusión o engaño en los usuarios sobre la verdadera procedencia del servicio o Contenidos; (II) suponga un acto de comparación o imitación desleal; (III) sirva para aprovechar la reputación de la marca y prestigio del Prestador de Servicios; o (IV) de cualquier otra forma resulte prohibido por la legislación vigente.'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'No se realizará, desde la página que introduce el enlace, ningún tipo de manifestación falsa, inexacta o incorrecta sobre el Prestador de Servicios, sus socios, empleados, clientes o sobre la calidad de los servicios que presta.'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'En ningún caso, se expresará en la página donde se ubique el enlace que el Prestador de Servicios ha facilitado su consentimiento para la inserción del enlace o que de otra forma patrocina, colabora, verifica o supervisa los servicios del remitente.'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'Está prohibida la utilización de cualquier marca denominativa, gráfica o mixta o cualquier otro signo distintivo del Prestador de Servicios, dentro de la página del remitente, salvo en los casos permitidos por la ley o expresamente autorizados por el Prestador de Servicios, y siempre que se permita, en estos casos, un enlace directo con el Portal en la forma establecida en esta cláusula.'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'La página que establezca el enlace deberá cumplir fielmente con la ley y no podrá en ningún caso disponer o enlazar con contenidos propios o de terceros que: (I) sean ilícitos, nocivos o contrarios a la moral y a las buenas costumbres (pornográficos, violentos, racistas, etc.); (II) induzcan o puedan inducir en el Usuario la falsa concepción de que el Prestador de Servicios suscribe, respalda, se adhiere o de cualquier manera apoya, las ideas, manifestaciones o expresiones, lícitas o ilícitas, del remitente; (III) resulten inapropiados o no pertinentes con la actividad del Prestador de Servicios en atención al lugar, contenidos y temática de la página web del remitente.'
              )}
            </Text>
          </Paragraph>

          <Title level={2} fontSize={title2FontSize}>
            4. {t('Exclusión de responsabilidad')}
          </Title>
          <Title level={3} fontSize={title3FontSize}>
            4.1 {t('De la calidad del servicio')}
          </Title>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'El acceso al Portal no implica la obligación por parte del Prestador de Servicios de controlar la ausencia de virus, gusanos o cualquier otro elemento informático dañino. Corresponde al Usuario, en todo caso, la disponibilidad de herramientas adecuadas para la detección y desinfección de programas informáticos dañinos.'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'El Prestador de Servicios no se responsabiliza de los daños producidos en los equipos informáticos de los Usuarios o de terceros durante la prestación del servicio del Portal.'
              )}
            </Text>
          </Paragraph>

          <Title level={3} fontSize={title3FontSize}>
            4.2 {t('De la disponibilidad del servicio')}
          </Title>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'El acceso al Portal requiere de servicios y suministros de terceros, incluidos el transporte a través de redes de telecomunicaciones cuya fiabilidad, calidad, continuidad y funcionamiento no corresponde al Prestador de Servicios. Por consiguiente, los servicios provistos a través del Portal pueden ser suspendidos, cancelados o resultar inaccesibles, con carácter previo o simultáneo a la prestación del servicio del Portal.'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'El Prestador de Servicios no responderá en ningún caso de los daños o perjuicios de cualquier tipo producidos para el Usuario, que traigan causa de fallos o desconexiones en las redes de telecomunicaciones que produzcan la suspensión, cancelación o interrupción del servicio del Portal durante la prestación del mismo o con carácter previo.'
              )}
            </Text>
          </Paragraph>

          <Title level={3} fontSize={title3FontSize}>
            4.3 {t('De los contenidos y servicios enlazados a través del Portal')}
          </Title>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'El servicio de acceso al Portal podría incluir dispositivos técnicos de enlace, directorios e incluso instrumentos de búsqueda que permiten al Usuario acceder a otras páginas y portales de Internet (en adelante, "Sitios Enlazados").'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'En estos casos, el Prestador de Servicios actúa como prestador de servicios de intermediación de conformidad con el artículo 17 de la Ley 34/2002, de 12 de julio, de Servicios de la Sociedad de la Información y el Comercio Electrónico (LSSI) y sólo será responsable de los contenidos y servicios suministrados en los Sitios Enlazados en la medida en que tenga conocimiento efectivo de la ilicitud de los mismos, y no haya desactivado el enlace con la diligencia debida.'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'En el supuesto de que el Usuario considere que existe un Sitio Enlazado con contenidos ilícitos o inadecuados podrá comunicárselo al Prestador de Servicios de acuerdo con el procedimiento y los efectos establecidos en la cláusula 5, sin que en ningún caso esta comunicación conlleve la obligación de retirar el correspondiente enlace.'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'En ningún caso, la existencia de Sitios Enlazados debe presuponer la existencia de acuerdos con los responsables o titulares de los mismos, ni la recomendación, promoción o identificación del Prestador de Servicios con las manifestaciones, contenidos o servicios que provistos.'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'El Prestador de Servicios no conoce los contenidos y servicios de los Sitios Enlazados y por tanto no se hace responsable por los daños producidos por la ilicitud, calidad, desactualización, indisponibilidad, error e inutilidad de los contenidos y/o servicios de los Sitios Enlazados ni por cualquier otro daño que no sea directamente imputable al Prestador de Servicios.'
              )}
            </Text>
          </Paragraph>

          <Title level={2} fontSize={title2FontSize}>
            5. {t('Comunicación de actividades de carácter ilícito e inadecuado')}
          </Title>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'En el caso de que el Usuario o cualquier otro usuario de Internet tuviera conocimiento de que los Sitios Enlazados remiten a páginas cuyos contenidos o servicios son ilícitos, nocivos, denigrantes, violentos o contrarios a la moral; o que alguna de la información incluida por los propios Usuarios, a través de los servicios ofrecidos en el Portal, tienen una consideración igual a la descrita anteriormente, Ud. podrá ponerse en contacto con el Prestador de Servicios indicando los siguientes extremos:'
              )}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              <ul className={styles.indentedUl}>
                <li>{t('Datos personales del comunicante: nombre, dirección, número de teléfono y dirección de correo electrónico;')}</li>
                <li>{t('Descripción de los hechos que revelan el carácter ilícito o inadecuado del Sitio Enlazado;')}</li>
                <li>
                  {t(
                    'En el supuesto de violación de derechos, tales como propiedad intelectual e industrial, los datos personales del titular del derecho infringido cuando sea persona distinta del comunicante. Asimismo, deberá aportar el título que acredite la legitimación del titular de los derechos y, en su caso, el de representación para actuar por cuenta del titular cuando sea persona distinta del comunicante;'
                  )}
                </li>
                <li>{t('Declaración expresa de que la información contenida en la reclamación es exacta.')}</li>
              </ul>
            </Text>
          </Paragraph>

          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'Si no se suministrase la información arriba indicada, no podrá considerarse la concurrencia del conocimiento efectivo, según lo dispuesto en la LSSI, de las actividades y/o contenidos indicados por el comunicante.'
              )}
            </Text>
          </Paragraph>

          <Title level={2} fontSize={title2FontSize}>
            6. {t('Legislación')}
          </Title>
          <Paragraph>
            <Text fontSize={normalTextFontSize}>
              {t(
                'El presente Aviso Legal se rige en todos y cada uno de sus extremos por la legislación española, y en caso de que fuera necesario, se someterá a los Tribunales de Madrid.'
              )}
            </Text>
          </Paragraph>
        </Typography>
      </div>
    </div>
  );
};

export default UseAgreement;
